<script setup lang="ts">
import { computed, onMounted, watch, ref } from 'vue'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import InlineMessage from 'primevue/inlinemessage'
import { useProfilesStore } from '@/store/modules/userProfile'
import { getInferredCurrency } from '@/utils/conductor.util'
import { Settings2, Calculator } from 'lucide-vue-next'

type BetFormat = 'decimal' | 'fraction'

const profilesStore = useProfilesStore()
const saveTimer = ref<number | null>(null)

const formatOptions = [
  { label: 'Decimal (e.g. 2.50)', value: 'decimal' },
  { label: 'Fraction (e.g. 6/4)', value: 'fraction' }
]

const currencyOptions = [
  { label: 'Euro (€)', value: 'EUR' },
  { label: 'Sterling (£)', value: 'GBP' },
  { label: 'Dollar ($)', value: 'USD' }
]

const betFormat = computed<BetFormat>({
  get: () => (profilesStore.profile.betFormat ?? 'decimal') as BetFormat,
  set: (val: BetFormat) => {
    profilesStore.profile.betFormat = val
  },
})

const preferredCurrency = computed({
  get: () => profilesStore.profile.preferredCurrency || getInferredCurrency(),
  set: (val: 'EUR' | 'GBP' | 'USD') => {
    profilesStore.profile.preferredCurrency = val
  },
})

async function save() {
  if (typeof window !== 'undefined' && saveTimer.value != null) {
    window.clearTimeout(saveTimer.value)
    saveTimer.value = null
  }
  const userId = profilesStore.profile.userId
  if (userId) {
    await profilesStore.saveToDB(userId)
    profilesStore.saveLocal()
  } else {
    profilesStore.saveLocal()
  }
}

watch([betFormat, preferredCurrency], async () => {
  if (typeof window !== 'undefined' && saveTimer.value != null) {
    window.clearTimeout(saveTimer.value)
  }
  saveTimer.value = typeof window !== 'undefined'
    ? window.setTimeout(async () => {
        await save()
      }, 300)
    : null
})

onMounted(() => {
  if (!profilesStore.profile.userId) {
    profilesStore.loadLocal()
  }
})

defineExpose({
  save
})
</script>

<template>
  <div class="space-y-10 pb-8">
    <section>
      <div class="mb-5">
        <div class="flex items-center gap-2">
          <Settings2 class="w-5 h-5 text-gray-400" />
          <h3 class="text-lg font-bold text-gray-900 dark:text-white">Betting Preferences</h3>
        </div>
        <p class="text-sm text-gray-500 mt-0.5 ml-7">Configure how odds and bets are shown in the builder</p>
      </div>

      <div class="overflow-hidden rounded-xl border border-border dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-2 md:p-6">
        <div class="max-w-md space-y-8">
          <div class="flex flex-col min-w-0">
            <div class="flex items-center gap-2 mb-2">
              <Calculator class="w-3.5 h-3.5 text-gray-400" />
              <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Preferred Odds Format</span>
            </div>
            
            <Select v-model="betFormat">
              <SelectTrigger class="w-full h-11 rounded-xl border-border dark:border-gray-800 bg-white dark:bg-gray-950 focus:ring-primary">
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent class="rounded-xl border-border dark:border-gray-800">
                <SelectItem v-for="opt in formatOptions" :key="opt.value" :value="opt.value" class="rounded-lg">
                  {{ opt.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div class="flex flex-col min-w-0">
            <div class="flex items-center gap-2 mb-2">
              <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Preferred Currency</span>
            </div>
            
            <Select v-model="preferredCurrency">
              <SelectTrigger class="w-full h-11 rounded-xl border-border dark:border-gray-800 bg-white dark:bg-gray-950 focus:ring-primary">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent class="rounded-xl border-border dark:border-gray-800">
                <SelectItem v-for="opt in currencyOptions" :key="opt.value" :value="opt.value" class="rounded-lg">
                  {{ opt.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            
            <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">
              Choose how bets and currencies are displayed. 
              Changes are saved automatically.
            </p>
          </div>

          <InlineMessage v-if="profilesStore.error" severity="error" class="w-full">{{ profilesStore.error }}</InlineMessage>
        </div>
      </div>
    </section>
  </div>
</template>